import { executeFunctionByName, parseOptions } from './utils';

type AjaxTableOptions = {
  add?: {
    selector: string;
    url?: string;
    parameterSelector: string;
    callback?: string;
  };
  remove?: {
    selector: string;
    callback?: string;
  };
  table: {
    selector: string;
    position?: 'top' | 'bottom';
  };
};

const defaultOptions: AjaxTableOptions = {
  add: {
    selector: '[data-ajt-add-button]',
    parameterSelector: '[data-ajt-add-parameter]'
  },
  remove: {
    selector: '[data-ajt-remove-button]'
  },
  table: {
    selector: 'table',
    position: 'bottom'
  }
};

export function initAjaxTables(): void {
  const sections = document.querySelectorAll<HTMLElement>('[data-ajax-table]');
  sections.forEach((section) => {
    initRemoveButtons(section);
    initAddButtons(section);
  });
}

export function initAddButtons(section: HTMLElement): void {
  const opts = parseOptions<AjaxTableOptions>(section.dataset.ajaxTable ?? null, defaultOptions);

  if (!opts?.add) return;

  const addBtns = section.querySelectorAll<HTMLElement>(opts.add.selector);

  addBtns.forEach((addButton) => {
    const parent = addButton.parentNode;
    if (!parent) return;
    const newButton = addButton.cloneNode(true) as HTMLElement;
    parent.replaceChild(newButton, addButton);
    newButton.addEventListener('click', (e) => handleAddButton(section, e));
  });
}

function handleAddButton(section: HTMLElement, e: Event): void {
  e.preventDefault();
  e.stopPropagation();

  const options = parseOptions<AjaxTableOptions>(section.dataset.ajaxTable ?? null, defaultOptions);

  if (!options?.add) return;

  const url = options.add.url;
  const data = new FormData();
  const parameters = Array.from(
    section.querySelectorAll<HTMLElement>(options.add.parameterSelector)
  );

  parameters.forEach((parameter) => {
    const name =
      (parameter as HTMLInputElement).name ||
      parameter.getAttribute('name') ||
      '';
    const value =
      (parameter as HTMLInputElement).value ??
      (parameter as HTMLSelectElement).value ??
      (parameter as HTMLTextAreaElement).value ??
      '';
    if (name) data.append(name, String(value));
  });

  fetch(url!, {
    method: 'POST',
    body: new URLSearchParams(data as any)
  })
    .then((response) => {
      if (!response.ok) throw new Error(response.statusText || 'Request failed');
      return response.text();
    })
    .then((html) => {
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      const tbody = section.querySelector(`${options.table.selector} > tbody`);

      if (!tbody) return;

      switch (options.table.position) {
        case 'top':
          tbody.prepend(template.content);
          break;
        case 'bottom':
        default:
          tbody.appendChild(template.content);
          break;
      }

      resetParameters(section);
      initRemoveButtons(section);
      checkEmptyTable(section);
      checkCallback(options.add?.callback);
    })
    .catch((error) => {
      console.error(error);
    });
}

export function checkEmptyTable(section: HTMLElement): void {
  const options = parseOptions<AjaxTableOptions>(section.dataset.ajaxTable ?? null, defaultOptions);

  const tfoot = section.querySelector<HTMLElement>(`${options.table.selector} > tfoot`);
  const rows = section.querySelectorAll(`${options.table.selector} > tbody tr`);

  if (rows.length === 0) {
    tfoot?.classList.remove('hidden');
  } else {
    tfoot?.classList.add('hidden');
  }
}

export function initRemoveButtons(section: HTMLElement): void {
  const options = parseOptions<AjaxTableOptions>(section.dataset.ajaxTable ?? null, defaultOptions);

  if (!options?.remove) return;

  const removeBtns = section.querySelectorAll<HTMLElement>(options.remove.selector);
  removeBtns.forEach((removeBtn) => {
    const parent = removeBtn.parentNode;
    if (!parent) return;
    const newButton = removeBtn.cloneNode(true) as HTMLElement;
    parent.replaceChild(newButton, removeBtn);
    newButton.addEventListener('click', (e) => handleRemoveButton(section, e));
  });
}

function handleRemoveButton(section: HTMLElement, e: Event): void {
  e.preventDefault();
  e.stopPropagation();

  const options = parseOptions<AjaxTableOptions>(section.dataset.ajaxTable ?? null, defaultOptions);
  if (!options?.remove) return;

  const target = e.currentTarget as Element | null;
  const tr = target?.closest('tr');
  if (tr) tr.remove();

  checkEmptyTable(section);
  checkCallback(options.remove?.callback);
}

function resetParameters(section: HTMLElement): void {
  const options = parseOptions<AjaxTableOptions>(section.dataset.ajaxTable ?? null, defaultOptions);
  if (!options?.add) return;

  const parameters = Array.from(
    section.querySelectorAll<HTMLElement>(options.add.parameterSelector)
  );

  parameters.forEach((parameter) => {
    // reset standard form field value
    if ('value' in parameter) {
      try {
        (parameter as HTMLInputElement).value = '';
      } catch {
        // ignore if not settable
      }
    } else {
      // fallback to attribute
      parameter.setAttribute('value', '');
    }

    // support third-party attached instances (e.g. slimselect) stored on element
    const anyParam = parameter as any;
    if (anyParam?.slim && typeof anyParam.slim.setSelected === 'function') {
      anyParam.slim.setSelected('');
    }
  });
}

function checkCallback(callbackFunction?: string): void {
  if (callbackFunction) {
    executeFunctionByName(callbackFunction, window);
  }
}

initAjaxTables();
